import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Camera } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/components/AuthProvider";
import DocumentTitle from "@/components/DocumentTitle";
import SignInPrompt from "@/pages/member/SignInPrompt";
import useSafeBack from "@/hooks/useSafeBack";

/**
 * Iter 28 · Vania Msg 226 — Edit Profile page.
 *
 * Fields: display_name (required), @handle (unique), bio (240 chars), photo.
 * Photo upload posts to POST /api/me/photo (multipart) and returns the
 * canonical photo_url that gets stored on the user record.
 *
 * Non-photo fields are saved via PUT /api/auth/profile.
 */
const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api` });
const META = "var(--ww-text-muted)";
const LINE = "var(--ww-line)";
const CARD = "var(--ww-surface)";
const YELLOW = "#FFD60A";

function bearer() {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function absUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${API}${u}`;
}

export default function EditProfilePage() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const onBack = useSafeBack("/app/me");
  const fileRef = useRef(null);

  const [displayName, setDisplayName] = useState("");
  const [handle, setHandle] = useState("");
  const [bio, setBio] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  // Wave 12.17 (Vania 1 Oct) · gender lives on the user doc and is editable
  // from profile settings. No pop-up elsewhere. "" = unset, which is fine.
  const [gender, setGender] = useState("");

  useEffect(() => {
    if (!user) return;
    setDisplayName(user.display_name || user.name || "");
    setHandle((user.handle || "").replace(/^@+/, ""));
    setBio(user.bio || "");
    setPhotoUrl(user.photo_url || user.picture || "");
    // Load gender from /me/gender/status (not on user doc public view).
    client.get("/me/gender/status", { headers: bearer() })
      .then((r) => { if (r.data?.gender) setGender(r.data.gender); })
      .catch(() => {});
  }, [user]);

  if (!loading && !user) {
    return <SignInPrompt title="edit your profile"
                         line="sign in first, then come back to make your profile feel like you."
                         next="/app/me/edit" testId="edit-profile-signin" />;
  }

  const onPickPhoto = () => fileRef.current?.click();

  const onPhotoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("photo must be under 6MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await client.post("/me/photo", fd, {
        headers: { ...bearer(), "Content-Type": "multipart/form-data" },
      });
      setPhotoUrl(r.data.photo_url);
      toast.success("photo updated");
      if (refresh) refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail?.error || "could not upload. try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onSave = async () => {
    const errs = {};
    const dn = displayName.trim();
    if (!dn) errs.display_name = "your name is required.";
    if (dn.length > 120) errs.display_name = "keep it under 120 characters.";
    const h = handle.trim().replace(/^@+/, "").toLowerCase();
    if (h && !/^[a-z0-9_]{2,30}$/.test(h)) errs.handle = "2-30 letters, numbers or underscores.";
    if (bio.length > 240) errs.bio = "keep it under 240 characters.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      await client.put("/auth/profile", {
        display_name: dn,
        handle: h,
        bio: bio.trim(),
      }, { headers: bearer() });
      // Wave 12.17 · persist gender via /me/gender (empty = clear).
      try {
        await client.put("/me/gender", { gender: gender || "" }, { headers: bearer() });
      } catch { /* non-fatal */ }
      toast.success("profile saved");
      if (refresh) refresh();
      nav("/app/me");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("handle")) {
        setErrors({ handle: detail });
      } else {
        toast.error(typeof detail === "string" ? detail : "could not save. try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const initial = (displayName || handle || "?").slice(0, 1).toUpperCase();

  return (
    <div className="flex h-full flex-col" data-testid="edit-profile-page"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <DocumentTitle title="edit profile · watu wako" />

      <header className="flex items-center gap-3 px-5 pt-4">
        <button type="button" onClick={onBack} data-testid="edit-profile-back" aria-label="back"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--ww-text-muted)",
                         fontFamily: "Arial", fontSize: 13, padding: "10px 4px", minHeight: 44,
                         background: "transparent", border: 0, cursor: "pointer" }}>
          <ArrowLeft size={16} /> back
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-5 pb-24" style={{ overscrollBehavior: "contain" }}>
        <div className="mx-auto" style={{ maxWidth: 560 }}>
          <h1 className="lowercase"
              style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.03em",
                       fontSize: 32, lineHeight: 1.02, marginTop: 8 }}>
            edit your profile
          </h1>
          <p style={{ fontFamily: "Arial", fontSize: 14, color: META, marginTop: 4 }}>
            this is how you show up on your profile and your watu card in discover.
          </p>

          {/* Photo */}
          <div className="mt-6 flex items-center gap-4" data-testid="edit-profile-photo-row">
            {photoUrl ? (
              <img data-testid="edit-profile-photo-preview" src={absUrl(photoUrl)} alt=""
                   style={{ width: 96, height: 96, borderRadius: 999, objectFit: "cover",
                            border: `2px solid ${LINE}` }} />
            ) : (
              <div data-testid="edit-profile-photo-placeholder"
                   style={{ width: 96, height: 96, borderRadius: 999, background: YELLOW,
                            color: "#1A1A30", display: "grid", placeItems: "center",
                            fontFamily: "Poppins", fontWeight: 800, fontSize: 34 }}>
                {initial}
              </div>
            )}
            <div>
              <button type="button" data-testid="edit-profile-photo-pick" onClick={onPickPhoto}
                      disabled={uploading}
                      style={{ padding: "10px 16px", borderRadius: 999,
                               background: CARD, border: `1px solid ${YELLOW}`, color: YELLOW,
                               fontFamily: "Poppins", fontWeight: 600, fontSize: 13,
                               textTransform: "lowercase", cursor: uploading ? "wait" : "pointer",
                               display: "inline-flex", alignItems: "center", gap: 6, minHeight: 44 }}>
                <Camera size={16} /> {uploading ? "uploading…" : (photoUrl ? "change photo" : "add photo")}
              </button>
              <p style={{ fontFamily: "Arial", fontSize: 12, color: META, marginTop: 6 }}>
                jpeg, png or webp · up to 6MB
              </p>
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                   onChange={onPhotoChange} style={{ display: "none" }} data-testid="edit-profile-photo-input" />
          </div>

          <Field label="your name" hint="how people will see you (e.g. vania n.)" error={errors.display_name}>
            <input data-testid="edit-profile-display-name" value={displayName}
                   onChange={(e) => setDisplayName(e.target.value)}
                   placeholder="your name" maxLength={120}
                   style={inputStyle(!!errors.display_name)} />
          </Field>

          <Field label="@handle" hint="letters, numbers and _ · 2-30 chars · must be unique" error={errors.handle}>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
                             fontFamily: "Arial", color: META, fontSize: 15 }}>@</span>
              <input data-testid="edit-profile-handle" value={handle}
                     onChange={(e) => setHandle(e.target.value.replace(/^@+/, ""))}
                     placeholder="yourhandle" maxLength={30}
                     style={{ ...inputStyle(!!errors.handle), paddingLeft: 28 }} />
            </div>
          </Field>

          <Field label="short bio" hint={`${bio.length}/240 characters`} error={errors.bio}>
            <textarea data-testid="edit-profile-bio" value={bio}
                      onChange={(e) => setBio(e.target.value.slice(0, 240))}
                      placeholder="a line or two about you — what you're into, where you hang."
                      rows={3}
                      style={{ ...inputStyle(!!errors.bio), resize: "vertical", minHeight: 96, paddingTop: 12 }} />
          </Field>

          {/* Wave 12.17 (Vania 1 Oct) · gender lives here. No pop-up elsewhere.
              helps us bring the right events to the right people. never shown on your profile. */}
          <Field label="gender (optional)"
                 hint="helps us bring the right events to the right people. never shown on your profile.">
            <div className="flex flex-wrap gap-2" data-testid="edit-profile-gender">
              {[
                { k: "woman",             l: "woman" },
                { k: "man",               l: "man" },
                { k: "non_binary",        l: "non-binary" },
                { k: "prefer_not_to_say", l: "prefer not to say" },
              ].map((g) => {
                const on = gender === g.k;
                return (
                  <button key={g.k} type="button"
                          data-testid={`edit-profile-gender-${g.k}`}
                          onClick={() => setGender(on ? "" : g.k)}
                          style={{
                            padding: "10px 14px", borderRadius: 999,
                            background: on ? "var(--ww-text)" : "transparent",
                            color: on ? "var(--ww-ground)" : "var(--ww-text)",
                            border: `1px solid ${on ? "var(--ww-text)" : LINE}`,
                            fontFamily: "Poppins", fontWeight: 600, fontSize: 13,
                            textTransform: "lowercase", cursor: "pointer", minHeight: 40,
                          }}>
                    {g.l}
                  </button>
                );
              })}
              {gender && (
                <button type="button" data-testid="edit-profile-gender-clear"
                        onClick={() => setGender("")}
                        style={{ padding: "10px 10px", background: "transparent", border: 0,
                                 color: META, fontFamily: "Arial", fontSize: 12,
                                 textDecoration: "underline", cursor: "pointer" }}>
                  clear
                </button>
              )}
            </div>
          </Field>

          <div className="mt-8 flex items-center justify-end gap-2">
            <button type="button" data-testid="edit-profile-cancel" onClick={onBack}
                    style={{ padding: "12px 20px", borderRadius: 999, background: "transparent",
                             border: `1px solid ${LINE}`, color: "var(--ww-text)",
                             fontFamily: "Poppins", fontWeight: 600, fontSize: 14,
                             textTransform: "lowercase", cursor: "pointer", minHeight: 44 }}>
              cancel
            </button>
            <button type="button" data-testid="edit-profile-save" onClick={onSave} disabled={saving}
                    style={{ padding: "12px 24px", borderRadius: 999, background: YELLOW, color: "#1A1A30",
                             border: `1px solid ${YELLOW}`,
                             fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
                             textTransform: "lowercase", cursor: saving ? "wait" : "pointer",
                             minHeight: 44 }}>
              {saving ? "saving…" : "save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function inputStyle(error) {
  return {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 12,
    background: "var(--ww-sunken)",
    border: `1px solid ${error ? "#FF6B9D" : "var(--ww-line)"}`,
    color: "var(--ww-text)",
    fontFamily: "Arial",
    fontSize: 15,
    outline: "none",
    minHeight: 44,
  };
}

function Field({ label, hint, error, children }) {
  return (
    <div className="mt-5">
      <label style={{ display: "block", fontFamily: "Poppins", fontWeight: 600,
                      textTransform: "lowercase", fontSize: 13, color: "var(--ww-text)", marginBottom: 6 }}>
        {label}
      </label>
      {children}
      {error ? (
        <p style={{ fontFamily: "Arial", fontSize: 12, color: "#FF6B9D", marginTop: 4 }}>{error}</p>
      ) : hint ? (
        <p style={{ fontFamily: "Arial", fontSize: 12, color: META, marginTop: 4 }}>{hint}</p>
      ) : null}
    </div>
  );
}
